import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import React, { FC } from 'react';
import { BiTrendingUp } from 'react-icons/bi';
import { useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import RenderIf from '@/components/Common/RenderIf';
import { Course } from '@/components/Courses/CoursesCard';
import BookCarousel from '@/components/Layout/Header/Search/Suggestions/BookCarousel';
import BundleCarousel from '@/components/Layout/Header/Search/Suggestions/BundleCarousel';
import CourseCarousel from '@/components/Layout/Header/Search/Suggestions/CourseCarousel';
import EmptySearchSection from '@/components/Layout/Header/Search/Suggestions/EmptySearchSection';
import OrangeCarousel from '@/components/Layout/Header/Search/Suggestions/OrangeCarousel';
import SchoolCarousel from '@/components/Layout/Header/Search/Suggestions/SchoolCarousel';

import { countryAtom } from '@/atoms/countryState';
import { Book } from '@/utils/interfaces/Library/Books';

import { ISchool } from '@/types/schools';

interface Props {
  className?: string;
  mode?: 'tranding' | 'suggestions';
  books?: Book[];
  oranges?: Book[];
  courses?: Course[];
  bundles?: Book[];
  schools?: ISchool[];

  onClickBook?: () => void;
  onClickTrending?: () => void;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 8,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const Suggestions: FC<Props> = ({
  className,
  mode,
  books,
  oranges,
  courses,
  bundles,
  onClickBook,
  onClickTrending,
  schools,
}) => {
  const countryState = useRecoilValue(countryAtom);
  const isIndia = countryState?.selectedCountry
    ? countryState.selectedCountry.code === 'IN'
    : true;
  const displayOranges = isIndia ? oranges : [];

  const list: { name: string; link: string }[] = [
    {
      name: 'Trading Mastermind',
      link: 'https://zebralearn.com/books/trading-mastermind-hard-copy-65a28e0ef2089eb795218609/',
    },
    {
      name: 'The Power of Emotional Marketing',
      link: 'https://zebralearn.com/books/the-power-of-emotional-marketing-3-678893199c11931be2531cb6/',
    },
    {
      name: 'Startupology',
      link: 'https://zebralearn.com/books/startupology-2-67fca6a39a7e6a98f39a2df5/',
    },
    {
      name: '51 Trading Strategies',
      link: 'https://zebralearn.com/books/51-trading-strategies-hardcopy-65be19a9f99ff072edcb42eb/',
    },
    {
      name: 'The Consulting Way',
      link: 'https://zebralearn.com/books/the-consulting-way-hardcover-66a4a5f2f67101f287e2287c/',
    },
    {
      name: 'The CFO Playbook',
      link: 'https://zebralearn.com/books/the-cfo-playbook-3-6882289a77fcd5e7403f3d3b/',
    },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: -16, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.98 }}
      transition={{
        duration: 0.22,
        ease: [0.16, 1, 0.3, 1], // premium easing
      }}
      className={twMerge(
        'z-50 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-100px)] scroll-primary absolute xl:top-[38.5px] sm:top-[30.5px] rounded-b-[12px] left-[-1.5px] w-[calc(100%+3px)] bg-grayZl-925 border-[1.5px] border-grayZl-900 border-t-[1.5px]',
        className,
      )}
    >
      <RenderIf condition={mode === 'tranding'}>
        <div>
          <p className='xl:text-xl/[24px] text-xs/[20px] font-semibold text-white tracking-[-2%] xl:pt-5 pt-3 xl:px-5 px-3 xl:pb-[11px] pb-[3px]'>
            Trending on Zebralearn
          </p>
          <motion.div
            variants={containerVariants}
            initial='hidden'
            animate='visible'
            className='pb-3'
          >
            {list.map((i, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Link key={index} href={i.link}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickTrending?.();
                    }}
                    className='flex items-center gap-[14px] xl:px-5 px-3 hover:bg-grayZl-900 cursor-pointer transition-colors xl:py-[9px] py-1.5'
                  >
                    <BiTrendingUp className='text-white xl:w-[18px] w-[16px] xl:h-[18px] h-[16px]' />
                    <p className='text-white xl:text-base/6 text-xs/5 font-normal'>
                      {i.name}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </RenderIf>
      <RenderIf condition={mode === 'suggestions'}>
        <motion.div
          className='md:pb-5 pb-3'
          variants={containerVariants}
          initial='hidden'
          animate='visible'
        >
          <RenderIf
            condition={
              books?.length ||
              displayOranges?.length ||
              courses?.length ||
              bundles?.length ||
              schools?.length
                ? true
                : false
            }
          >
            <p className='xl:text-xl/[24px] text-xs/[20px] font-semibold text-white tracking-[-2%] xl:pt-5 pt-3 xl:px-5 md:px-5 px-3 xl:pb-5 md:pb-2 pb-2.5'>
              Search Results
            </p>
          </RenderIf>
          <AnimatePresence>
            <RenderIf
              condition={
                !books?.length &&
                !displayOranges?.length &&
                !courses?.length &&
                !bundles?.length &&
                !schools?.length
              }
            >
              <EmptySearchSection />
            </RenderIf>
          </AnimatePresence>
          <RenderIf condition={books?.length ? true : false}>
            <motion.div variants={itemVariants}>
              <div className='md:px-5 px-3'>
                <BookCarousel onClickBook={onClickBook} books={books} />
              </div>
            </motion.div>
          </RenderIf>
          <RenderIf
            condition={
              books?.length &&
              (displayOranges?.length || courses?.length || bundles?.length || schools?.length)
                ? true
                : false
            }
          >
            <div className='w-full h-[1.5px] bg-grayZl-900 my-3' />
          </RenderIf>
          <RenderIf condition={displayOranges?.length ? true : false}>
            <motion.div variants={itemVariants}>
              <div className='md:px-5 px-3'>
                <OrangeCarousel onClickBook={onClickBook} oranges={displayOranges} />
              </div>
            </motion.div>
          </RenderIf>
          <RenderIf
            condition={
              displayOranges?.length &&
              (courses?.length || bundles?.length || schools?.length)
                ? true
                : false
            }
          >
            <div className='w-full h-[1.5px] bg-grayZl-900 my-3' />
          </RenderIf>
          <RenderIf condition={courses?.length ? true : false}>
            <motion.div variants={itemVariants}>
              <div className='md:px-5 px-3'>
                <CourseCarousel onClickBook={onClickBook} courses={courses} />
              </div>
            </motion.div>
          </RenderIf>
          <RenderIf
            condition={
              courses?.length && (bundles?.length || schools?.length)
                ? true
                : false
            }
          >
            <div className='w-full h-[1.5px] bg-grayZl-900 my-3' />
          </RenderIf>
          <RenderIf condition={bundles?.length ? true : false}>
            <motion.div variants={itemVariants}>
              <div className='md:px-5 px-3'>
                <BundleCarousel onClickBook={onClickBook} bundles={bundles} />
              </div>
            </motion.div>
          </RenderIf>
          <RenderIf
            condition={bundles?.length && schools?.length ? true : false}
          >
            <div className='w-full h-[1.5px] bg-grayZl-900 my-3' />
          </RenderIf>
          <RenderIf condition={schools?.length ? true : false}>
            <motion.div variants={itemVariants}>
              <div className='md:px-5 px-3'>
                <SchoolCarousel onClickBook={onClickBook} schools={schools} />
              </div>
            </motion.div>
          </RenderIf>
        </motion.div>
      </RenderIf>
    </motion.div>
  );
};

export default Suggestions;
